import { useEffect, useState } from "react";
import { Plus, ShieldOff, Trash2, Wand2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const blank = { term: "", replacement: "", notes: "", active: true };

/** Settings → Vendor Brands. Names on this list are rewritten out of release
 *  notes and app tiles at read time; stored text is never touched. */
export function VendorBrandsAdmin({ canManage }) {
  const [items, setItems] = useState([]);
  const [fallback, setFallback] = useState("our platform provider");
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [sample, setSample] = useState("");
  const [preview, setPreview] = useState("");

  const load = () =>
    api.get("/vendor-brands").then(({ data }) => {
      setItems(data.items || []);
      setFallback(data.default_replacement || "our platform provider");
    }).catch(() => toast.error("Couldn't load the vendor brand list"));

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!draft.term.trim()) return toast.error("Enter the brand name");
    setBusy(true);
    try {
      await api.post("/vendor-brands", draft);
      setDraft(blank);
      await load();
      toast.success(`${draft.term} is now hidden from the UI`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add that brand");
    } finally { setBusy(false); }
  };

  const save = async (row, patch) => {
    const next = { ...row, ...patch };
    setItems((list) => list.map((i) => (i.brand_id === row.brand_id ? next : i)));
    try {
      await api.put(`/vendor-brands/${row.brand_id}`, {
        term: next.term, replacement: next.replacement, notes: next.notes, active: next.active,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that change");
      load();
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Stop hiding "${row.term}"? Its name will show in release notes and app tiles again.`)) return;
    await api.delete(`/vendor-brands/${row.brand_id}`);
    await load();
    toast.success(`${row.term} removed from the list`);
  };

  const runPreview = async () => {
    const { data } = await api.post("/vendor-brands/preview", { text: sample });
    setPreview(data.text || "");
  };

  return (
    <Card className="max-w-4xl space-y-6 border-slate-200 p-6 shadow-sm" data-testid="vendor-brands-admin">
      <div className="flex items-start gap-3">
        <ShieldOff className="mt-0.5 h-5 w-5 shrink-0 text-rose-500" />
        <div>
          <h3 className="font-heading text-sm font-semibold text-slate-800">Vendor brands</h3>
          <p className="text-xs text-slate-400">
            Names on this list never reach a reader. They are rewritten out of release notes and app
            tiles as pages load — nothing stored is changed, so removing a name here brings it back.
            Leave the replacement blank to use &ldquo;{fallback}&rdquo;.
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-3 py-2 font-semibold">Brand</th>
              <th className="px-3 py-2 font-semibold">Readers see</th>
              <th className="px-3 py-2 font-semibold">Note</th>
              <th className="px-3 py-2 font-semibold">Hiding</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.brand_id} className="border-t border-slate-100"
                data-testid={`vendor-brand-row-${row.brand_id}`}>
                <td className="px-3 py-2">
                  <Input defaultValue={row.term} disabled={!canManage}
                    onBlur={(e) => e.target.value !== row.term && save(row, { term: e.target.value })}
                    className="h-8 font-medium" data-testid={`vendor-brand-term-${row.brand_id}`} />
                </td>
                <td className="px-3 py-2">
                  <Input defaultValue={row.replacement} placeholder={fallback} disabled={!canManage}
                    onBlur={(e) => e.target.value !== row.replacement && save(row, { replacement: e.target.value })}
                    className="h-8" data-testid={`vendor-brand-replacement-${row.brand_id}`} />
                </td>
                <td className="px-3 py-2">
                  <Input defaultValue={row.notes} placeholder="optional" disabled={!canManage}
                    onBlur={(e) => e.target.value !== row.notes && save(row, { notes: e.target.value })}
                    className="h-8" data-testid={`vendor-brand-notes-${row.brand_id}`} />
                </td>
                <td className="px-3 py-2">
                  <Switch checked={!!row.active} disabled={!canManage}
                    onCheckedChange={(v) => save(row, { active: v })}
                    data-testid={`vendor-brand-active-${row.brand_id}`} />
                </td>
                <td className="px-3 py-2">
                  {canManage && (
                    <button type="button" onClick={() => remove(row)}
                      className="text-slate-300 transition-colors hover:text-rose-600"
                      data-testid={`vendor-brand-delete-${row.brand_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-slate-400"
                data-testid="vendor-brands-empty">No brands are being hidden yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {canManage && (
        <div className="flex flex-wrap items-end gap-2" data-testid="vendor-brand-add-row">
          <div className="w-48">
            <label className="text-xs text-slate-400">Brand name</label>
            <Input value={draft.term} onChange={(e) => setDraft({ ...draft, term: e.target.value })}
              placeholder="e.g. Acme Telecom" className="h-8" data-testid="vendor-brand-new-term" />
          </div>
          <div className="w-56">
            <label className="text-xs text-slate-400">Readers see</label>
            <Input value={draft.replacement} onChange={(e) => setDraft({ ...draft, replacement: e.target.value })}
              placeholder={fallback} className="h-8" data-testid="vendor-brand-new-replacement" />
          </div>
          <div className="w-56">
            <label className="text-xs text-slate-400">Note</label>
            <Input value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="optional" className="h-8" data-testid="vendor-brand-new-notes" />
          </div>
          <Button size="sm" onClick={add} disabled={busy} data-testid="vendor-brand-add-btn">
            <Plus className="mr-1 h-4 w-4" /> Add brand
          </Button>
        </div>
      )}

      <div className="space-y-2 border-t border-slate-100 pt-4">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          Check a piece of text
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={sample} onChange={(e) => setSample(e.target.value)}
            placeholder="Paste any wording to see what a reader would get"
            className="h-8 max-w-xl" data-testid="vendor-brand-preview-input" />
          <Button size="sm" variant="outline" onClick={runPreview} data-testid="vendor-brand-preview-btn">
            <Wand2 className="mr-1 h-4 w-4" /> Check
          </Button>
        </div>
        {preview && (
          <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700"
            data-testid="vendor-brand-preview-out">{preview}</p>
        )}
      </div>
    </Card>
  );
}
